import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useDropTransactions } from 'services/repositories/transactions/transactions';
import { NotificationType, useNotificationStore } from 'stores/notifications/notifications';
import { useAnalytics } from 'support/helpers/analytics/analytics';
import { EMPTY_FUNCTION } from 'support/helpers/const/const';
import { Popup } from 'components/Display/Popup/Popup';
import { Button } from 'components/Form/Button/Button';
import { Input } from 'components/Form/Input/Input/Input';
import { LoadingLogo } from 'components/Loading/LoadingLogo';

export type DropTransactionProps = {
  transactionIds: Array<string>;
  open: boolean;
  onClose?: () => void;
  onComplete: () => void;
};

export function DropTransactionModal({
  transactionIds,
  open,
  onClose = EMPTY_FUNCTION,
  onComplete,
}: DropTransactionProps) {
  const { t } = useTranslation();
  const { track } = useAnalytics();
  const { addNotification } = useNotificationStore();
  const [dropTransactionReason, setDropTransactionReason] = useState<string>('');
  const [isLoading, setIsLoading] = useState(false);
  const { mutate } = useDropTransactions();

  const transactionItems = useMemo(() => transactionIds.map((id) => ({ transactionId: id })), [transactionIds]);

  useEffect(() => {
    // reset state when modal is closed
    // controlling loading state separately because of flickering when closing
    if (open) {
      setIsLoading(false);
      setDropTransactionReason('');
    }
  }, [open]);

  const drop = async (reason: string) => {
    const payload = { items: transactionItems, reason };
    setIsLoading(true);
    mutate(payload, {
      onSuccess: (response) => {
        track('transactions_dropped', {
          reason,
          transaction_count: transactionIds.length,
          transaction_ids: transactionIds,
        });
        if (response.success) {
          if ((response.data?.failed ?? []).length > 0) {
            addNotification(
              {
                title: t('transactions:show.sections.errors.dropTransactions.warn', {
                  failed: response.data?.failed?.length,
                  total: transactionIds.length,
                }),
                subtitle: t('transactions:show.sections.errors.dropTransactions.failed_description'),
              },
              NotificationType.error,
            );
          } else {
            addNotification(
              {
                title: t('transactions:show.sections.errors.dropTransactions.success', {
                  count: transactionIds.length,
                }),
              },
              NotificationType.success,
            );
          }
        } else {
          addNotification(
            {
              title: t('transactions:show.sections.errors.dropTransactions.failed'),
              subtitle: t('transactions:show.sections.errors.dropTransactions.failed_description'),
            },
            NotificationType.error,
          );
        }
        onComplete();
      },
      onError: () => {
        addNotification(
          {
            title: t('transactions:show.sections.errors.dropTransactions.failed'),
            subtitle: t('transactions:show.sections.errors.dropTransactions.failed_description'),
          },
          NotificationType.error,
        );
      },
      onSettled: () => {
        onComplete();
      },
    });
  };

  const PrimaryButton = () => (
    <Button
      onClick={() => {
        drop(dropTransactionReason);
      }}
      loading={isLoading}
      analyticsId="transactions:drop_submit"
    >
      <div data-testid="action-button">{t('transactions:bulk_discard_modal.action')}</div>
    </Button>
  );

  const CancelButton = ({ cancelButtonRef }: { cancelButtonRef: React.RefObject<HTMLButtonElement | null> }) => (
    <Button
      onClick={onComplete}
      disabled={isLoading}
      variant="secondary"
      ref={cancelButtonRef}
      analyticsId="transactions:drop_cancel"
    >
      <div data-testid="cancel-button">{t('transactions:show.sections.errors.dropTransaction.cancelText')}</div>
    </Button>
  );

  return (
    <>
      <Popup
        CancelButton={isLoading ? () => null : CancelButton}
        PrimaryButton={isLoading ? () => null : PrimaryButton}
        title={!isLoading ? t('transactions:bulk_discard_modal.title', { count: transactionIds.length }) : ''}
        subtitle={!isLoading ? t('transactions:bulk_discard_modal.subtitle', { count: transactionIds.length }) : ''}
        open={open}
        onClose={onClose}
        testId="drop-transaction-modal"
      >
        {isLoading ? (
          <div className="flex h-full flex-col items-center justify-center text-center">
            <div className="mb-6 w-11">
              <LoadingLogo />
            </div>

            <p className="text-gray-900">{t('transactions:bulk_discard_modal.loading.title')}</p>
            <p className="text-gray-500">{t('transactions:bulk_discard_modal.loading.subtitle')}</p>
          </div>
        ) : (
          <>
            <div className="text-left text-sm">
              {t('transactions:bulk_discard_modal.inputHeading')} <span className="text-red-500">*</span>
            </div>

            <Input
              name="dropTransactionReason"
              value={dropTransactionReason}
              onChange={(event) => {
                setDropTransactionReason(event.target.value);
              }}
              placeholder={t('transactions:show.sections.errors.dropTransaction.placeholder')}
              className="my-2 w-full"
            />
          </>
        )}
      </Popup>
    </>
  );
}
